import { useEffect, useState } from 'react';
import '../styles/modules-supervisor-dashboard.css';
import {
  platformApi,
  type PlatformAccountResult,
  type PlatformDashboardResult,
} from './platform-api';
import { PrototypeIcon, type PrototypeIconName } from './PrototypeIcon';

interface MetricDefinition {
  readonly label: string;
  readonly value: number | null;
  readonly detail: string;
  readonly icon: PrototypeIconName;
  readonly tone?: 'red' | 'purple' | 'green';
}

export function SupervisorDashboard(): React.JSX.Element {
  const [dashboard, setDashboard] = useState<PlatformDashboardResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    void platformApi
      .getDashboard()
      .then((result) => {
        if (!active) return;
        setDashboard(result);
      })
      .catch(() => {
        if (!active) return;
        setError('Le tableau de bord de supervision ne peut pas être chargé.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <main className="app-content supervisor-workspace supervisor-dashboard">
      <header className="dashboard-heading supervisor-dashboard__heading">
        <div>
          <p className="dashboard-heading__eyebrow">Supervision de la plateforme</p>
          <h1>Tableau de bord</h1>
          <p className="dashboard-heading__intro">
            Une vue d’ensemble des accès, de l’activité et de la santé administrative de Tosho, sans
            exposer les données métier des organisations.
          </p>
        </div>
      </header>

      {loading ? <p className="supervisor-dashboard__status">Chargement des indicateurs…</p> : null}
      {error ? (
        <div className="supervisor-danger-error tosho-feedback is-error" role="alert">
          {error}
        </div>
      ) : null}
      {dashboard ? <DashboardContent dashboard={dashboard} /> : null}
    </main>
  );
}

function DashboardContent({
  dashboard,
}: {
  readonly dashboard: PlatformDashboardResult;
}): React.JSX.Element {
  const { metrics } = dashboard;
  const cards: readonly MetricDefinition[] = [
    {
      label: 'Administrateurs',
      value: metrics.totalAdministrators,
      detail: 'rattachements Administrateur courants',
      icon: 'users-round',
      tone: 'red',
    },
    {
      label: 'Organisations',
      value: metrics.totalOrganizations,
      detail: 'espaces enregistrés',
      icon: 'briefcase-business',
    },
    {
      label: 'Comptes',
      value: metrics.totalAccounts,
      detail: 'identités d’organisation',
      icon: 'network',
    },
    {
      label: 'Comptes actifs',
      value: metrics.activeAccounts,
      detail: 'identités actuellement actives',
      icon: 'shield-check',
      tone: 'green',
    },
    {
      label: 'Comptes inactifs',
      value: metrics.inactiveAccounts,
      detail: 'suspendus ou archivés',
      icon: 'users-round',
    },
    {
      label: 'Invitations',
      value: metrics.pendingInvitations,
      detail: 'invitations encore valides',
      icon: 'history',
      tone: 'purple',
    },
    {
      label: 'Nouveaux comptes',
      value: metrics.accountsCreatedLast30Days,
      detail: 'créés sur les 30 derniers jours',
      icon: 'sparkles',
    },
    {
      label: 'Projets',
      value: metrics.projectCount,
      detail: metrics.projectCount === null ? 'source non raccordée' : 'projets au total',
      icon: 'folder-kanban',
      tone: 'red',
    },
    {
      label: 'Serveurs surveillés',
      value: metrics.monitoredServerCount,
      detail: metrics.monitoredServerCount === null ? 'source non raccordée' : 'serveurs suivis',
      icon: 'database',
      tone: 'green',
    },
  ];

  return (
    <>
      <section className="dashboard-summary supervisor-dashboard__summary" aria-label="Indicateurs">
        {cards.map((card) => (
          <MetricCard key={card.label} definition={card} />
        ))}
      </section>

      <section className="supervisor-dashboard__panels" aria-label="Synthèses d’activité">
        <ActivityPanel
          eyebrow="Créations"
          title="Administrateurs les plus récents"
          accounts={dashboard.recentAdministrators}
          empty="Aucun Administrateur à afficher."
          dateMode="created"
        />
        <ActivityPanel
          eyebrow="Activité"
          title="Administrateurs les plus actifs"
          accounts={dashboard.mostActiveAdministrators}
          empty="Aucune activité de connexion disponible."
          dateMode="activity"
        />
        <ActivityPanel
          eyebrow="À surveiller"
          title="Comptes les moins récemment utilisés"
          accounts={dashboard.leastRecentlyActiveAccounts}
          empty="Aucun compte actif à afficher."
          dateMode="activity"
        />
      </section>
    </>
  );
}

function MetricCard({ definition }: { readonly definition: MetricDefinition }): React.JSX.Element {
  const toneClass = definition.tone ? ` dashboard-summary-card--${definition.tone}` : '';

  return (
    <article className={`dashboard-summary-card supervisor-dashboard__metric${toneClass}`}>
      <div className="dashboard-summary-card__header">
        <span className="dashboard-summary-card__label">{definition.label}</span>
        <span className="dashboard-summary-card__icon" aria-hidden="true">
          <PrototypeIcon name={definition.icon} />
        </span>
      </div>
      <strong>{definition.value === null ? '—' : definition.value.toLocaleString('fr-FR')}</strong>
      <small>{definition.detail}</small>
    </article>
  );
}

function ActivityPanel({
  eyebrow,
  title,
  accounts,
  empty,
  dateMode,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly accounts: readonly PlatformAccountResult[];
  readonly empty: string;
  readonly dateMode: 'created' | 'activity';
}): React.JSX.Element {
  return (
    <article className="dashboard-panel supervisor-dashboard__panel">
      <header className="dashboard-panel__header">
        <div>
          <p className="dashboard-panel__eyebrow">{eyebrow}</p>
          <h2>{title}</h2>
        </div>
      </header>
      {accounts.length === 0 ? (
        <div className="dashboard-empty-state dashboard-empty-state--compact">
          <span className="dashboard-empty-state__mark" aria-hidden="true">
            <PrototypeIcon name="users-round" />
          </span>
          <strong>{empty}</strong>
        </div>
      ) : (
        <ul className="supervisor-dashboard__activity-list">
          {accounts.map((account) => (
            <li key={account.userId}>
              <span className="supervisor-dashboard__person">
                <strong>
                  {account.givenName} {account.familyName}
                </strong>
                <small>{account.organization?.displayName ?? 'Sans rattachement'}</small>
              </span>
              <span className="supervisor-dashboard__activity-date">
                {dateMode === 'created'
                  ? formatDate(account.createdAt)
                  : account.lastActivityAt === null
                    ? 'Jamais utilisé'
                    : formatDate(account.lastActivityAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Date indisponible';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}
